/* Public customer-facing invoice payment page.
 *
 * Customer URL: pay.userevstar.com/invoice/:token (prod)
 *                pay.staging.userevstar.com/invoice/:token (staging)
 * Frontend route: /invoice/:token
 *
 * Design intent
 * -------------
 *   This is the only customer-facing surface in the whole feature.
 *   Treat it like a Stripe / Square hosted invoice — the customer
 *   should land, immediately understand who is asking for money,
 *   how much, by when, and have one obvious thing to do.
 *
 *   Visual hierarchy, top to bottom:
 *     1. Merchant brand (small, sets context)
 *     2. Amount due — HERO, the single biggest thing on the page
 *     3. Due date (tinted red if overdue)
 *     4. Pay CTA — big primary button
 *     5. Itemisation — clean rows, no chrome
 *     6. Totals — tabular, compact
 *     7. Notes if any
 *     8. Secure-payment footer
 *
 *   Status-driven states:
 *     - SENT     : full page above
 *     - OVERDUE  : same, with red "X days overdue" subtitle
 *     - PARTIAL  : same, amount due shows remaining balance
 *     - PAID     : success state — checkmark, "Paid on …", no CTA
 *     - VOID/404 : friendly empty state
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Loader2, CheckCircle2, AlertCircle, ArrowRight,
  Lock, Calendar
} from 'lucide-react';
import axios from '../../axiosConfig';
import './PayInvoice.css';

/* ─── Helpers ────────────────────────────────────────────────── */

const fmtMoney = (currency, n) =>
  new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: currency || 'NGN',
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const fmtDateLong = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

const daysBetween = (a, b) =>
  Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86_400_000);

function effectiveStatus(invoice) {
  if (!invoice) return null;
  if (invoice.status === 'SENT' && invoice.dueDate && new Date(invoice.dueDate) < new Date()) {
    return 'OVERDUE';
  }
  return invoice.status;
}

function balanceDue(invoice) {
  return Math.max(0, Number(invoice.total) - Number(invoice.amountPaid));
}

/* ─── States ────────────────────────────────────────────────── */

function LoadingState() {
  return (
    <div className="payinv-state">
      <Loader2 size={28} className="payinv-spin payinv-state-icon" />
    </div>
  );
}

function NotFoundState() {
  return (
    <div className="payinv-state">
      <div className="payinv-state-icon payinv-state-icon--muted">
        <AlertCircle size={28} />
      </div>
      <h1>Invoice not available</h1>
      <p>The link may have expired or been cancelled. Please contact the merchant for an updated link.</p>
    </div>
  );
}

function PaidState({ invoice }) {
  return (
    <div className="payinv-paid">
      <div className="payinv-paid-icon"><CheckCircle2 size={36} /></div>
      <h1>Paid</h1>
      <p className="payinv-paid-amount">{fmtMoney(invoice.currency, invoice.total)}</p>
      <p className="payinv-paid-meta">
        Received on {fmtDateLong(invoice.paidAt)}
        {invoice.paymentMethod && ` via ${String(invoice.paymentMethod).toLowerCase()}`}.
      </p>
      <div className="payinv-paid-receipt">
        <div className="payinv-paid-receipt-num">Receipt {invoice.invoiceNumber}</div>
        <div className="payinv-paid-receipt-from">Billed by {invoice.merchant?.name || 'Merchant'}</div>
      </div>
    </div>
  );
}

/* ─── Main ──────────────────────────────────────────────────── */

export default function PayInvoice() {
  // Route is declared as /invoice/:invoiceRef (shared with InvoicePage
  // via InvoiceRoute). Rename to `token` so the rest of the component
  // reads naturally — this is a merchant-invoice payment token, not
  // an order invoice ref.
  const { invoiceRef: token } = useParams();
  const [params] = useSearchParams();
  const cameFromPaystack = params.get('status') === 'success';

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [paying, setPaying] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [verifyTried, setVerifyTried] = useState(false);

  // load({silent: true}) refetches without toggling the spinner —
  // used by the post-Paystack polling so the page doesn't flicker
  // every 2.5s while we wait for the webhook to mark the invoice PAID.
  const load = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setErrorMsg(null);
      const { data } = await axios.get(`/pay/invoice/${token}`);
      if (!data?.invoice) { setNotFound(true); return; }
      setInvoice(data.invoice);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) setNotFound(true);
      else if (!silent) setErrorMsg('We couldn’t load this invoice. Please refresh and try again.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // ── Verify on initial load ────────────────────────────────────
  // Runs ONCE on first successful invoice load when status isn't
  // final. Uses the reference from the URL if available (post-Paystack
  // callback), otherwise sends an empty body — the backend then
  // falls back to invoice.paystackReference if one is stored. This
  // catches:
  //   1. Callback case (?reference=invoice_<id>_<epoch>) — verifies
  //      the just-completed payment.
  //   2. Bare-URL-reload case — customer closed the success tab,
  //      came back, status is still SENT in DB; backend uses stored
  //      reference and verifies any pending payment automatically.
  // No-op if there's nothing to verify (backend returns
  // { nothingToVerify: true }).
  useEffect(() => {
    if (verifyTried) return;
    if (!invoice) return;
    if (invoice.status === 'PAID' || invoice.status === 'VOID') return;
    setVerifyTried(true);
    const reference = params.get('reference') || params.get('trxref');
    const body = reference ? { reference } : {};
    axios.post(`/pay/invoice/${token}/verify`, body)
      .then(() => load({ silent: true }))
      .catch((err) => {
        console.warn('[PayInvoice] verify failed:', err?.response?.data || err?.message);
      });
  }, [invoice, params, token, load, verifyTried]);

  // ── Poll while waiting for Paystack to settle ─────────────────
  // Only runs when the customer came back from Paystack with
  // status=success. Silent polls (no spinner flicker). After 30s,
  // pollTimedOut flips so the UI shows the "still processing" hint
  // with a Check-again button.
  useEffect(() => {
    if (!cameFromPaystack || !invoice || invoice.status === 'PAID') return;
    const interval = setInterval(() => load({ silent: true }), 2_500);
    const timeout  = setTimeout(() => {
      clearInterval(interval);
      setPollTimedOut(true);
    }, 30_000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [cameFromPaystack, invoice, load]);

  const handlePay = async () => {
    try {
      setPaying(true);
      setErrorMsg(null);
      const { data } = await axios.post(`/pay/invoice/${token}/pay`);
      if (data?.authorizationUrl) {
        window.location.href = data.authorizationUrl;
        return;
      }
      setErrorMsg('Could not start payment. Please try again.');
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || 'Could not start payment. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  if (loading)              return <PayInvoiceShell><LoadingState /></PayInvoiceShell>;
  if (notFound || !invoice) return <PayInvoiceShell><NotFoundState /></PayInvoiceShell>;

  const status   = effectiveStatus(invoice);
  const isPaid   = status === 'PAID';
  const isVoid   = status === 'VOID';
  const balance  = balanceDue(invoice);
  const overdue  = status === 'OVERDUE';

  if (isPaid) return <PayInvoiceShell><PaidState invoice={invoice} /></PayInvoiceShell>;
  if (isVoid) return <PayInvoiceShell><NotFoundState /></PayInvoiceShell>;

  const overdueDays = overdue ? Math.max(0, daysBetween(invoice.dueDate, new Date())) : 0;

  return (
    <PayInvoiceShell>
      <header className="payinv-brand">
        {invoice.merchant?.logo
          ? <img src={invoice.merchant.logo} alt="" className="payinv-brand-logo" />
          : <div className="payinv-brand-logo payinv-brand-logo--ph">
              {invoice.merchant?.name?.charAt(0)?.toUpperCase() || 'M'}
            </div>}
        <div className="payinv-brand-text">
          <div className="payinv-brand-from">Invoice from</div>
          <div className="payinv-brand-name">{invoice.merchant?.name || 'Merchant'}</div>
        </div>
        <div className="payinv-brand-num">{invoice.invoiceNumber}</div>
      </header>

      <section className="payinv-hero">
        <div className="payinv-hero-label">Amount due</div>
        <div className="payinv-hero-amount">{fmtMoney(invoice.currency, balance)}</div>
        <div className={`payinv-hero-due ${overdue ? 'is-overdue' : ''}`}>
          <Calendar size={13} />
          {overdue
            ? <span>Overdue by {overdueDays} day{overdueDays === 1 ? '' : 's'} · was due {fmtDateLong(invoice.dueDate)}</span>
            : <span>Due {fmtDateLong(invoice.dueDate)}</span>}
        </div>
      </section>

      {cameFromPaystack ? (
        // Just got back from Paystack with status=success. Show a
        // confirming banner while the webhook flips the invoice to
        // PAID. If 30s passes without confirmation, flip to a
        // friendlier "check back in a minute" message + a manual
        // refresh button so the customer isn't stuck on a spinner.
        <div className="payinv-cta">
          {pollTimedOut ? (
            <>
              <div className="payinv-confirming payinv-confirming--waiting">
                <span>Your payment is still being processed.</span>
              </div>
              <button
                type="button"
                className="payinv-btn-pay payinv-btn-pay--ghost"
                onClick={() => { setPollTimedOut(false); load(); }}
              >
                Check again
              </button>
              <div className="payinv-cta-methods">
                If this persists for more than a few minutes, please contact the merchant.
              </div>
            </>
          ) : (
            <>
              <div className="payinv-confirming">
                <Loader2 size={16} className="payinv-spin" />
                <span>Confirming your payment…</span>
              </div>
              <div className="payinv-cta-methods">
                Paystack typically confirms within a few seconds.
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="payinv-cta">
          <button
            type="button"
            className="payinv-btn-pay"
            onClick={handlePay}
            disabled={paying}
          >
            {paying
              ? <><Loader2 size={16} className="payinv-spin" /> Connecting to Paystack…</>
              : <>Pay {fmtMoney(invoice.currency, balance)} <ArrowRight size={16} /></>}
          </button>
          {errorMsg && <div className="payinv-error" role="alert">{errorMsg}</div>}
          <div className="payinv-cta-methods">
            Card · Bank transfer · USSD
          </div>
        </div>
      )}

      <section className="payinv-block">
        <div className="payinv-block-head">
          <span>Item</span>
          <span>Total</span>
        </div>
        <ul className="payinv-items">
          {(invoice.lineItems || []).map((li, i) => {
            const qty  = Number(li.qty) || 0;
            const unit = Number(li.unitPrice) || 0;
            return (
              <li key={i}>
                <div className="payinv-item-info">
                  <div className="payinv-item-desc">{li.description}</div>
                  <div className="payinv-item-sub">{qty} × {fmtMoney(invoice.currency, unit)}</div>
                </div>
                <div className="payinv-item-total">{fmtMoney(invoice.currency, qty * unit)}</div>
              </li>
            );
          })}
        </ul>

        <div className="payinv-totals">
          <div><span>Subtotal</span><span>{fmtMoney(invoice.currency, invoice.subtotal)}</span></div>
          {Number(invoice.taxTotal) > 0 && (
            <div><span>Tax</span><span>{fmtMoney(invoice.currency, invoice.taxTotal)}</span></div>
          )}
          {Number(invoice.amountPaid) > 0 && (
            <div><span>Already paid</span><span>−{fmtMoney(invoice.currency, invoice.amountPaid)}</span></div>
          )}
          <div className="payinv-totals-grand">
            <span>Total due</span>
            <span>{fmtMoney(invoice.currency, balance)}</span>
          </div>
        </div>
      </section>

      {invoice.customer?.name && (
        <section className="payinv-billto">
          <div className="payinv-billto-label">Billed to</div>
          <div className="payinv-billto-name">{invoice.customer.name}</div>
          <div className="payinv-billto-meta">
            {invoice.customer.email}
            {invoice.customer.phone && ` · ${invoice.customer.phone}`}
          </div>
        </section>
      )}

      {invoice.notes && (
        <section className="payinv-notes">
          <div className="payinv-notes-label">Note from merchant</div>
          <p>{invoice.notes}</p>
        </section>
      )}

      <footer className="payinv-foot">
        <div className="payinv-foot-secure"><Lock size={11} /> Payments processed securely by Paystack</div>
        <div className="payinv-foot-brand">Powered by <strong>Revstar</strong></div>
      </footer>
    </PayInvoiceShell>
  );
}

/* Card shell — keeps the gradient background and centred card
   layout consistent across every state (loading / not-found / paid /
   pay-now). */
function PayInvoiceShell({ children }) {
  return (
    <div className="payinv-page">
      <div className="payinv-card">{children}</div>
    </div>
  );
}
